<template>
  <div>
    <LoadingSpinner v-if="$apollo.loading" />
    <EmptyMessage v-if="articles?.length === 0">
      No study notes found for the current passage.
    </EmptyMessage>
    <ArticleDetail
      v-else-if="selectedArticleUri"
      :key="selectedArticleUri"
      :articleUri="selectedArticleUri"
      :hasMore="articles?.length > 1"
      @select-article-uri="uri => (selectedArticleUri = uri)"
    />
    <template v-else-if="articlesByCollection">
      <div class="passage-ref">{{ passageRef }}</div>
      <ArticleList
        :collections="articlesByCollection"
        :show-collection-header="true"
        @select-article-uri="uri => (selectedArticleUri = uri)"
      />
    </template>
  </div>
</template>
<script>
  import mixins from '@/mixins';
  import gql from 'graphql-tag';
  import { DEFAULT_GROUP_KEY } from '@/store/constants';
  import LoadingSpinner from '@/symphony/widgets/components/LoadingSpinner.vue';
  import EmptyMessage from '@/symphony/widgets/components/EmptyMessage.vue';
  import ArticleList from '@/symphony/widgets/articles-widget/ArticleList.vue';
  import ArticleDetail from '@/symphony/widgets/articles-widget/ArticleDetail.vue';

  export default {
    name: 'TyndaleBibleDictionaryArticlesWidget',
    mixins: [mixins.InputDataMixin, mixins.RefAdapterMixin, mixins.LocalizationMixin],
    components: {
      LoadingSpinner,
      EmptyMessage,
      ArticleDetail,
      ArticleList,
    },
    props: {
      doric: {
        inputs: {
          label: 'Tyndale Bible Dictionary Articles',
          osisRef: {
            value: 'osisRef',
            groupKey: DEFAULT_GROUP_KEY,
          },
          selectedTokenIds: {
            groupKey: DEFAULT_GROUP_KEY,
            value: 'selectedTokenIds',
          },
        },
      },
    },
    data() {
      return {
        selectedArticleUri: null,
        passageRef: null,
      };
    },
    watch: {
      usfmRef: {
        handler(newValue) {
          if (newValue) {
            this.passageRef = newValue;
          }
        },
        immediate: true,
      },
      verses(newValue) {
        if (newValue?.length) {
          this.passageRef = newValue[0].usfmRef;
        }
      },
    },
    computed: {
      parsedSelectedTokenIds() {
        try {
          return JSON.parse(this.selectedTokenIds);
        } catch (e) {
          return [];
        }
      },
      noSelectedTokenIds() {
        return (
          !Array.isArray(this.parsedSelectedTokenIds) || this.parsedSelectedTokenIds.length === 0
        );
      },
      localizedArticles() {
        if (!this.articles) {
          return null;
        }
        return this.articles
          .map(article => {
            const { uri, id, category, collection, title } = article;
            const fallbackTitle = this.getLocalizedOrFallback(article, 'title');
            const description = this.getLocalizedOrFallback(article, 'description');
            return {
              id,
              uri,
              title,
              fallbackTitle,
              description,
              category,
              collection,
            };
          })
          .sort((a, b) => a.id.localeCompare(b.id))
          .sort((a, b) => (a?.category?.order ?? 0) - (b?.category?.order ?? 0));
      },
      articlesByCollection() {
        const collections = this.localizedArticles
          ? this.localizedArticles.reduce((acc, article) => {
              const collection =
                this.getLocalizedOrFallback(article.collection, 'label') || 'Uncategorized';
              if (!acc[collection]) {
                acc[collection] = [];
              }
              acc[collection].push(article);
              return acc;
            }, {})
          : {};
        return Object.entries(collections).sort((a, b) => a[0].localeCompare(b[0]));
      },
    },
    apollo: {
      articles: {
        query: gql`
          fragment tyndaleBibleDictionaryArticleFields on Article {
            id
            uri
            title(language: $language, allowFallback: false)
            description(language: $language)
            localizations {
              language
              title
              description
            }
            collection {
              id
              uri
              label
            }
            category {
              name
              order
            }
          }
          query TyndaleBibleDictionaryArticles($passageRef: String!, $language: String = "eng") {
            keyTerms: articles(
              filters: {
                scriptureReference: { usfmRef: $passageRef, scope: VERSE }
                collection: { AND: { uri: { inList: ["TyndaleBibleDictionary"] } } }
              }
              pagination: { limit: 100, offset: 0 }
            ) {
              ...tyndaleBibleDictionaryArticleFields
            }
          }
        `,
        variables() {
          return {
            passageRef: this.passageRef,
            language: this.lang,
          };
        },
        skip() {
          return !this.osisRef;
        },
        update(data) {
          return data.keyTerms;
        },
      },
      verses: {
        query: gql`
          query WordTokenVerse($filters: WordTokenFilter) {
            wordTokens(filters: $filters) {
              id
              verse {
                usfmRef
              }
            }
          }
        `,
        variables() {
          return {
            filters: { id: { inList: this.parsedSelectedTokenIds } },
          };
        },
        skip() {
          return this.noSelectedTokenIds;
        },
        update(data) {
          return data.wordTokens.map(token => token.verse);
        },
      },
    },
  };
</script>

<style type="text/css" scoped>
  .passage-ref {
    margin-bottom: 1em;
  }
</style>
